import { useCallback, useState } from 'react'
import type {
  ContactGroup,
  ContactGroupDetail,
  CreateContactGroupPayload,
  DynamicListPreview,
  Ruleset,
  UpdateContactGroupPayload,
} from 'types/communications'
import type { MutationResult, QueryResult } from 'utils/api'
import { qs } from 'utils/api'

import { useApiQuery } from '../useApiQuery'
import { useFetchApi } from '../useFetchApi'

export interface ContactGroupsParams {
  skip?: number
  take?: number
}

export function useContactGroups(params?: ContactGroupsParams): QueryResult<ContactGroup[]> {
  return useApiQuery<ContactGroup[]>(`/api/contacts/groups${qs(params)}`)
}

export function useContactGroup(id: string | undefined): QueryResult<ContactGroupDetail> {
  return useApiQuery<ContactGroupDetail>(id ? `/api/contacts/groups/${id}` : null)
}

export function useCreateContactGroup(): MutationResult<ContactGroup, [CreateContactGroupPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (payload: CreateContactGroupPayload) => {
      setLoading(true)
      try {
        return await fetchApi<ContactGroup>('/api/contacts/groups', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useUpdateContactGroup(): MutationResult<ContactGroup, [string, UpdateContactGroupPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string, payload: UpdateContactGroupPayload) => {
      setLoading(true)
      try {
        return await fetchApi<ContactGroup>(`/api/contacts/groups/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useDeleteContactGroup(): MutationResult<ContactGroup, [string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string) => {
      setLoading(true)
      try {
        return await fetchApi<ContactGroup>(`/api/contacts/groups/${id}`, { method: 'DELETE' })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useAddGroupContact(): MutationResult<unknown, [string, string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (groupId: string, contactId: string) => {
      setLoading(true)
      try {
        return await fetchApi(`/api/contacts/groups/${groupId}/contacts`, {
          method: 'POST',
          body: JSON.stringify({ contactId }),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useRemoveGroupContact(): MutationResult<unknown, [string, string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (groupId: string, contactId: string) => {
      setLoading(true)
      try {
        return await fetchApi(`/api/contacts/groups/${groupId}/contacts/${contactId}`, { method: 'DELETE' })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function usePreviewDynamicList(): MutationResult<DynamicListPreview, [Ruleset]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (ruleset: Ruleset) => {
      setLoading(true)
      try {
        return await fetchApi<DynamicListPreview>('/api/contacts/groups/preview', {
          method: 'POST',
          body: JSON.stringify({ ruleset }),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}
